import { useEffect, useState, type FormEvent } from 'react';
import { toast } from 'sonner';
import { AlertCircle, Loader2 } from 'lucide-react';
import {
  mapStoreDetailsError,
  useTranslation,
  useUpdateStoreManualOrderApproval,
} from '@dukanti/shared';
import type { Store, StoreDetailsErrorKey, TranslationKey } from '@dukanti/shared';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { supabase } from '@/lib/supabase';
import { useNavBlocker } from '@/hooks/use-nav-blocker';

export interface ManualOrderApprovalFormProps {
  readonly store: Store;
}

export function ManualOrderApprovalForm({
  store,
}: ManualOrderApprovalFormProps): React.JSX.Element {
  const { t, dir } = useTranslation();
  const [enabled, setEnabled] = useState(store.manual_order_approval_enabled);
  const [submitError, setSubmitError] = useState<StoreDetailsErrorKey | null>(null);
  const updateMutation = useUpdateStoreManualOrderApproval(supabase);
  const isDirty = enabled !== store.manual_order_approval_enabled;

  useNavBlocker(isDirty);

  useEffect(() => {
    setEnabled(store.manual_order_approval_enabled);
    setSubmitError(null);
  }, [store.id, store.manual_order_approval_enabled]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (!isDirty || updateMutation.isPending) return;
    setSubmitError(null);
    try {
      await updateMutation.mutateAsync({ storeId: store.id, enabled });
      toast.success(t('admin.storeSettings.manualApproval.savedToast' as TranslationKey));
    } catch (err) {
      setSubmitError(mapStoreDetailsError(err));
    }
  }

  return (
    <form
      dir={dir}
      onSubmit={(event) => void handleSubmit(event)}
      className="mt-6"
      data-testid="manual-order-approval-form"
    >
      <section className="bg-card border-border rounded-xl border p-6 shadow-sm sm:p-8">
        <h2 className="font-display text-foreground text-start text-xl font-bold">
          {t('admin.storeSettings.manualApproval.title' as TranslationKey)}
        </h2>
        <p className="text-muted-foreground mt-2 text-start text-sm">
          {t('admin.storeSettings.manualApproval.description' as TranslationKey)}
        </p>

        <div className="bg-border my-4 h-px" aria-hidden="true" />

        <Label
          htmlFor="manual-order-approval-enabled"
          className="border-border bg-background flex cursor-pointer items-start gap-3 rounded-lg border p-4 text-start"
        >
          <input
            id="manual-order-approval-enabled"
            type="checkbox"
            className="border-input text-admin-lime focus:ring-admin-lime mt-1 size-4 rounded"
            checked={enabled}
            disabled={updateMutation.isPending}
            onChange={(event) => setEnabled(event.target.checked)}
            data-testid="manual-order-approval-checkbox"
          />
          <span className="min-w-0">
            <span className="text-foreground block text-sm font-medium">
              {t('admin.storeSettings.manualApproval.checkboxLabel' as TranslationKey)}
            </span>
            <span className="text-muted-foreground mt-1 block text-sm">
              {t(
                enabled
                  ? ('admin.storeSettings.manualApproval.enabledHint' as TranslationKey)
                  : ('admin.storeSettings.manualApproval.disabledHint' as TranslationKey),
              )}
            </span>
          </span>
        </Label>

        {submitError ? (
          <div
            role="alert"
            className="border-destructive/30 bg-destructive/10 text-destructive mt-4 flex items-start gap-2 rounded-lg border p-3 text-start text-sm"
            data-testid="manual-order-approval-error"
          >
            <AlertCircle className="mt-0.5 size-4 shrink-0" aria-hidden />
            <span>{t(submitError as TranslationKey)}</span>
          </div>
        ) : null}

        <div className="mt-5 flex flex-wrap items-center justify-end gap-2">
          {isDirty ? (
            <Button
              type="button"
              variant="ghost"
              disabled={updateMutation.isPending}
              onClick={() => setEnabled(store.manual_order_approval_enabled)}
            >
              {t('common.cancel')}
            </Button>
          ) : null}
          <Button type="submit" disabled={!isDirty || updateMutation.isPending}>
            {updateMutation.isPending ? (
              <>
                <Loader2 className="size-4 animate-spin" aria-hidden />
                <span>{t('admin.storeSettings.manualApproval.saving' as TranslationKey)}</span>
              </>
            ) : (
              <span>{t('common.save')}</span>
            )}
          </Button>
        </div>
      </section>
    </form>
  );
}
